import React, { useState, useEffect } from 'react';
import { Wifi, Users, Eye, EyeOff, Save, RefreshCw, Smartphone, Monitor, Laptop } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerWiFiManagement = ({ customer, deviceId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [wifiSettings, setWifiSettings] = useState(null);
  const [connectedDevices, setConnectedDevices] = useState([]);
  const [showPassword, setShowPassword] = useState({});
  const [editMode, setEditMode] = useState({});
  const [formData, setFormData] = useState({});
  const [deviceNotFound, setDeviceNotFound] = useState(false);

  useEffect(() => {
    if (deviceId) {
      setDeviceNotFound(false);
      fetchWifiSettings();
      fetchConnectedDevices();
    }
  }, [deviceId]);

  const getToken = () => {
    return localStorage.getItem('customer_token') || localStorage.getItem('token');
  };

  const fetchWifiSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/customer-wifi/${encodeURIComponent(deviceId)}/settings`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      if (response.data.success) {
        setWifiSettings(response.data.data);
        // Initialize form data
        const initialForm = {};
        response.data.data.wifi_networks?.forEach((wifi, idx) => {
          initialForm[wifi.index] = {
            ssid: wifi.ssid,
            password: ''
          };
        });
        setFormData(initialForm);
      }
    } catch (error) {
      // Suppress 404 spam — device not registered in ACS is a known state.
      if (error?.response?.status === 404) {
        setDeviceNotFound(true);
      } else {
        console.error('Error fetching WiFi settings:', error);
        toast.error('Gagal memuat pengaturan WiFi');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchConnectedDevices = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/customer-wifi/${encodeURIComponent(deviceId)}/connected`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      if (response.data.success) {
        setConnectedDevices(response.data.data.devices || []);
      }
    } catch (error) {
      if (error?.response?.status !== 404) {
        console.error('Error fetching connected devices:', error);
      }
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    const toastId = toast.loading("Menghubungi ONT...");
    
    try {
      const response = await axios.post(
        `${API_URL}/api/customer-wifi/${encodeURIComponent(deviceId)}/refresh`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` }, timeout: 35000 }
      );
      
      toast.dismiss(toastId);
      
      if (response.data.success) {
        toast.success(response.data.message || "Summon selesai!");
        fetchWifiSettings();
        fetchConnectedDevices();
      } else {
        toast.error(response.data.message || "Gagal summon");
      }
    } catch (error) {
      toast.dismiss(toastId);
      console.error('Refresh error:', error);
      toast.error("Timeout atau gagal koneksi. Coba lagi.");
    }
    setRefreshing(false);
  };

  const handleSaveWifi = async (wifiIndex) => {
    const data = formData[wifiIndex];
    if (!data?.ssid && !data?.password) {
      toast.error('Masukkan nama WiFi atau password baru');
      return;
    }

    if (data.password && data.password.length < 8) {
      toast.error('Password minimal 8 karakter');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/customer-wifi/${encodeURIComponent(deviceId)}/settings`,
        {
          wifi_index: wifiIndex,
          ssid: data.ssid || undefined,
          password: data.password || undefined
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      if (response.data.success) {
        toast.success('Pengaturan WiFi berhasil diupdate! Perubahan akan diterapkan dalam 1-5 menit.');
        setEditMode({ ...editMode, [wifiIndex]: false });
        // Clear password field after save
        setFormData({
          ...formData,
          [wifiIndex]: { ...formData[wifiIndex], password: '' }
        });
        // Auto refresh after 2 seconds
        setTimeout(() => fetchWifiSettings(), 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan pengaturan');
    } finally {
      setSaving(false);
    }
  };

  const getDeviceIcon = (device) => {
    const hostname = device.hostname?.toLowerCase() || '';
    if (hostname.includes('iphone') || hostname.includes('android') || hostname.includes('realme') || hostname.includes('samsung') || hostname.includes('xiaomi') || hostname.includes('oppo') || hostname.includes('vivo')) {
      return <Smartphone className="w-5 h-5 text-blue-500" />;
    } else if (hostname.includes('laptop') || hostname.includes('macbook') || hostname.includes('notebook')) {
      return <Laptop className="w-5 h-5 text-gray-600" />;
    }
    return <Monitor className="w-5 h-5 text-gray-500" />;
  };

  if (deviceNotFound) {
    return (
      <div className="p-6 text-center" data-testid="wifi-device-not-found">
        <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-3">
          <Wifi className="w-6 h-6 text-amber-500" />
        </div>
        <h3 className="text-base font-semibold text-slate-800 mb-1">Device Tidak Terdaftar di ACS</h3>
        <p className="text-sm text-slate-500">Device ID <span className="font-mono">{deviceId}</span> tidak ditemukan di GenieACS.</p>
        <p className="text-xs text-slate-400 mt-2">Mohon Unlink dan Link ulang ke device ONT yang aktif via halaman Detail Pelanggan &gt; Devices.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-slate-600">Memuat pengaturan WiFi...</span>
      </div>
    );
  }

  const activeWifiNetworks = wifiSettings?.wifi_networks?.filter(wifi => wifi.enabled) || [];
  
  if (!wifiSettings || !activeWifiNetworks.length) {
    return (
      <div className="text-center py-12">
        <Wifi className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <h3 className="text-lg font-medium text-slate-700">Tidak ada WiFi aktif</h3>
        <p className="text-slate-500 mt-2">Semua jaringan WiFi sedang nonaktif</p>
        <Button onClick={handleRefresh} className="mt-4" disabled={refreshing}>
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh Data
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
          <Wifi className="w-6 h-6 text-blue-600" />
          Pengaturan WiFi
        </h2>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* WiFi Networks */}
      <div className="grid gap-4">
        {wifiSettings.wifi_networks.filter(wifi => wifi.enabled).map((wifi) => (
          <div key={wifi.index} className="bg-gradient-to-br from-white to-blue-50 border border-blue-100 rounded-xl p-5 shadow-md">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    wifi.band === '5GHz' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {wifi.band}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    wifi.enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {wifi.enabled ? 'Aktif' : 'Nonaktif'}
                  </span>
                </div>
                <h3 className="text-lg font-semibold mt-2">{wifi.ssid}</h3>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Users className="w-4 h-4" />
                <span>{wifi.connected_count} perangkat</span>
              </div>
            </div>

            {/* Current Settings */}
            <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
              <div>
                <span className="text-slate-500">Channel:</span>
                <span className="ml-2 font-medium">{wifi.channel || 'Auto'}</span>
              </div>
              <div>
                <span className="text-slate-500">Password:</span>
                <span className="ml-2 font-medium font-mono">{wifi.password || "(tidak tersedia)"}</span>
              </div>
              <div>
                <span className="text-slate-500">Security:</span>
                <span className="ml-2 font-medium">{wifi.security}</span>
              </div>
            </div>

            {/* Edit Form */}
            {editMode[wifi.index] ? (
              <div className="space-y-3 pt-4 border-t">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Nama WiFi (SSID)
                  </label>
                  <Input
                    value={formData[wifi.index]?.ssid || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      [wifi.index]: { ...formData[wifi.index], ssid: e.target.value }
                    })}
                    placeholder="Masukkan nama WiFi baru"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Password Baru
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword[wifi.index] ? 'text' : 'password'}
                      value={formData[wifi.index]?.password || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        [wifi.index]: { ...formData[wifi.index], password: e.target.value }
                      })}
                      placeholder="Minimal 8 karakter (kosongkan jika tidak ingin ganti)"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowPassword({ ...showPassword, [wifi.index]: !showPassword[wifi.index] })}
                    >
                      {showPassword[wifi.index] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button 
                    onClick={() => handleSaveWifi(wifi.index)}
                    disabled={saving}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Menyimpan...' : 'Simpan'}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setEditMode({ ...editMode, [wifi.index]: false })}
                  >
                    Batal
                  </Button>
                </div>
              </div>
            ) : (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setEditMode({ ...editMode, [wifi.index]: true })}
                className="mt-2"
              >
                Ubah Pengaturan
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Connected Devices */}
      <div className="bg-gradient-to-br from-white to-blue-50 border border-blue-100 rounded-xl p-5 shadow-md">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-green-600" />
          Perangkat Terkoneksi ({connectedDevices.length})
        </h3>
        
        {connectedDevices.length === 0 ? (
          <p className="text-slate-500 text-center py-4">Tidak ada perangkat yang terkoneksi</p>
        ) : (
          <div className="space-y-3">
            {connectedDevices.map((device, idx) => (
              <div key={idx} className="flex items-center justify-between py-3 border-b last:border-0">
                <div className="flex items-center gap-3">
                  {getDeviceIcon(device)}
                  <div>
                    <p className="font-medium text-slate-800">{device.hostname}</p>
                    <p className="text-sm text-slate-500">{device.mac}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-slate-700">{device.ip}</p>
                  <p className={`text-xs ${device.connection_type === 'WiFi' ? 'text-blue-600' : 'text-green-600'}`}>
                    {device.connection_type}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-4 shadow-sm">
        <p className="text-sm text-blue-800">
          <strong>Catatan:</strong> Perubahan pengaturan WiFi akan diterapkan dalam 1-5 menit setelah disimpan. 
          Setelah perubahan diterapkan, Anda mungkin perlu menghubungkan ulang perangkat dengan password baru.
        </p>
      </div>
    </div>
  );
};

export default CustomerWiFiManagement;
